import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet, useHistory, useLocation } from 'umi';
import { PageContainer } from '@/bizComponents/pageContainer';
import { BreadCrumbsRoute } from '@/bizComponents/pageContainer/components/breadCrumbs';
import { Page404 } from '@/pages/404';
import { useAppContext } from '@/hooks/useAppContext';
import { getInitialPropsFromServer } from '@/ssr/getInitialPropsFromServer';
import {
  decodeSitemapUrl,
  encodeSitemapUrl,
  loadDataBySitemapUrlParams,
  Sitemap,
  SitemapPageData,
} from '@zbanx-coding/components-shared';
import { useCurrentRelationType } from '@/hooks/useCurrentRelationType';

export interface Props {
  initData?: SitemapPageData;
}

export const SitemapPage: React.FC<Props> = ({ initData }) => {
  const relationType = useCurrentRelationType();
  const { t } = useTranslation();
  const { language } = useAppContext();
  const { pathname, search } = useLocation();
  const history = useHistory();
  const params = decodeSitemapUrl(pathname, search);

  const historyPush = React.useCallback(
    (url: string) => {
      history.push(url);
    },
    [history],
  );

  if (params.invalidUrl) {
    return <Page404 />;
  }

  let title = '';
  let desc = '';

  const breadCrumbs: BreadCrumbsRoute[] | undefined = [
    {
      name: t('page.routes.home.title'),
      url: '/',
    },
  ];

  if (params.tab === 'categories') {
    title = t('sitemap.tab.category.seo.title');
    desc = t('sitemap.tab.category.seo.desc');
    breadCrumbs.push({ name: t('biz.app.footer.SiteMap') });
  } else if (params.tab === 'brands') {
    title = t('sitemap.tab.brand.seo.title');
    desc = t('sitemap.tab.brand.seo.desc');
    breadCrumbs.push({ name: t('biz.app.footer.SiteMap') });
  } else {
    if (params.tab === 'country' && params.country) {
      title = t('page.Youtube.page.SeoTitle', { params: params.country });
      desc = t('page.Youtube.page.metaTitle', { params: params.country });
      breadCrumbs.push({
        name: t('biz.app.footer.SiteMap'),
        url: encodeSitemapUrl({ tab: 'country' }),
      });
      breadCrumbs.push({
        name: t('page.routes.youtube.subordinateTitle'),
      });
    } else if (params.website && params.website) {
      title = t('page.Youtube.page.SeoTitle', { params: params.website });
      desc = t('page.Youtube.page.metaTitle', { params: params.website });
      breadCrumbs.push({
        name: t('biz.app.footer.SiteMap'),
        url: encodeSitemapUrl({ tab: 'website' }),
      });
      breadCrumbs.push({
        name: t('page.routes.youtube.subordinateTitle'),
      });
    } else {
      title = t('sitemap.tab.youtubers.seo.title');
      desc = t('sitemap.tab.youtubers.seo.desc');
      breadCrumbs.push({ name: t('biz.app.footer.SiteMap') });
    }
  }

  return (
    <PageContainer breadCrumbs={breadCrumbs}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
      </Helmet>
      <Sitemap
        language={language}
        historyPush={historyPush}
        pathname={pathname}
        search={search}
        initData={initData}
        relationType={relationType}
        onChange={(url) => {
          historyPush(url.url);
        }}
      />
    </PageContainer>
  );
};

getInitialPropsFromServer<Props>(
  SitemapPage,
  async ({ httpClient, pathname, search, language }) => {
    const params = decodeSitemapUrl(pathname, search);
    return {
      initData: params.invalidUrl
        ? undefined
        : await loadDataBySitemapUrlParams(params, httpClient, language),
    };
  },
);

export default SitemapPage;
